import md5 from 'crypto-js/md5';
import { Command, DEFAULT_SEND_MESSAGE_TIMEOUT } from '@tencent/docs-connection';
import { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import { ISocket } from '@tencent/slide-sdk-socket';

export interface Response {
    success: boolean;
    error?: {
        errorCode?: number;
        retCode?: number;
        message: string;
    }
}

type RequestingPool = {
    [requestKey: string]: boolean | undefined;
};

/**
 * 封装 slideApp.socket 请求
 * 1. promisfy sendMessage 方法，统一调用返回值（注意 socket 调用返回其实只是 ack，请求结果会通过 socket 事件下发）
 * 2. 维护 requestingPool 阻止重复请求
 * */
export default class RequestManager<T extends string> {
    public static makeResponse = (success: boolean, error?: Response['error']): Response => ({ success, error });

    /** 请求池，requestKey 使用请求参数（包括 cmd）生成 */
    private requestingPool: RequestingPool = {};

    /**
     * @param commonCustomParam Socket.sendMessage 参数 customParam
     * @param logPrefix log 前缀，形如 `[LOG]`
     */
    public constructor(private logPrefix: string, private socket: ISocket, private logger?: ILogger) {}

    /** 封装 Socket.sendMessage; 如果有参数列表完全相同的请求正在执行中，则拒绝当次请求 */
    public async send(
        cmd: T,
        data = {},
        customParam = {},
        taskId = 0,
        additionalFieldsToHead?: Record<string, string | number | boolean>,
    ): Promise<Response> {
        // hash 所有参数列表作为请求唯一标识
        const requestKey = md5(JSON.stringify({ cmd, data, customParam, taskId })).toString();
        if (this.requestingPool[requestKey]) {
            this.logger?.info(`[${this.logPrefix}] REQUEST CMD <${cmd}> failed, reason: command is requseting`);
            return RequestManager.makeResponse(false, { message: 'command is requseting' });
        }

        try {
            this.requestingPool[requestKey] = true;
            return await this.sendDirectly(cmd, data, customParam, taskId, additionalFieldsToHead);
        } finally {
            delete this.requestingPool[requestKey];
        }
    }

    /** 封装 Socket.sendMessage; 直接发送请求，不检查重复 */
    public async sendDirectly(
        cmd: T,
        data = {},
        customParam = {},
        taskId = 0,
        additionalFieldsToHead?: Record<string, string | number | boolean>,
    ): Promise<Response> {
        const param = {
            timeout: DEFAULT_SEND_MESSAGE_TIMEOUT,
            maxRetryCount: 3,
            waitAcceptCmd: [],
            ...customParam,
        };
        return new Promise((resolve) => {
            const onsend = (seq: number, cmd: string, data: string, result: any) => {
                const errorCode = result?.errCode;
                const retCode = result?.response?.onpost?.result;

                if (errorCode !== 0 || retCode !== 0) {
                    this.logger?.info(`[${this.logPrefix}] REQUEST CMD <${cmd}> failed, reason: post error ${errorCode}, retCode ${retCode}`);
                    return resolve(RequestManager.makeResponse(false, {
                        errorCode,
                        message: `post error ${errorCode}, retCode ${retCode}`,
                    }));
                }

                this.logger?.info(`[${this.logPrefix}] REQUEST CMD <${cmd}> succeeded`);
                return resolve(RequestManager.makeResponse(true));
            };

            const onError = (seq: number, cmd: string, data: string, result: any) => {
                // 请求被底层直接拒掉（未发送），直接 resolve
                if (result && result.errCode !== 0) {
                    const errorCode = result.errCode;
                    this.logger?.info(`[${this.logPrefix}] REQUEST CMD <${cmd}> failed, reason: send error ${errorCode}`);
                    return resolve(RequestManager.makeResponse(false, { errorCode, message: `send error ${errorCode}` }));
                }
                return resolve(RequestManager.makeResponse(true));
            };

            this.socket.sendMessage({
                cmd: cmd as Command,
                taskId,
                data: JSON.stringify(data),
                customParam: param,
                additionalFieldsToHead,
                onError,
                onsend,
            });
            // 其他情况 (返回为空[注意] or errCode === 0) 均视为已发送, 直接返回，等待 onSend 异步 resolve；
        });
    }
}
